import React, { useCallback, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { BsThreeDotsVertical } from 'react-icons/bs';
import type { Column } from 'react-table';

import useTitle from 'hooks/useTitle';
import { useUserPermissions } from 'hooks/query/useUserInfo';
import { useDeleteDesignationMutation, useDesignationsListQuery } from 'hooks/query/useDesignationQueries';

import history from 'utils/history';
import * as toast from 'utils/toast';
import { interpolate, pluralize } from 'utils/string';

import Table from 'components/common/table';
import PopOver from 'components/common/popover';
import EditDelete from 'components/common/editDelete';
import TableHeader from 'components/common/table/TableHeader';

import { DESIGNATIONS } from 'constants/permissions';
import { CREATE_DESIGNATION, UPDATE_DESIGNATION } from 'constants/routes';

import type { Designation as DesignationEntity } from 'interface/designation';

type PermissionKey = string;
type PermissionMap = Record<PermissionKey, boolean>;

type DesignationRow = Required<Pick<DesignationEntity, 'id' | 'name'>> &
  Pick<DesignationEntity, 'description' | 'status'> & {
    sn: number;
  };

type TableRowInfo = {
  original?: DesignationRow;
};

type CellProps = {
  value: number;
  original: DesignationRow;
};

type ActionCellProps = {
  original: DesignationRow;
};

function Designation() {
  useTitle('Designations');

  const [columnToSortBy, setColumnToSortBy] = useState('sn');
  const [isAscendingOrdered, setIsAscendingOrdered] = useState(true);
  const [openPopoverId, setOpenPopoverId] = useState<number | null>(null);

  const permissions = useUserPermissions() as PermissionMap;

  const { data: designations = [], isLoading } = useDesignationsListQuery();
  const { mutate: deleteDesignation } = useDeleteDesignationMutation();

  const tableData = useMemo<DesignationRow[]>(() => {
    return designations
      .filter((item): item is DesignationEntity & { id: number; name: string } => {
        return typeof item.id === 'number' && typeof item.name === 'string' && item.name.length > 0;
      })
      .map((item, index) => ({
        id: item.id,
        name: item.name,
        description: item.description ?? null,
        status: item.status,
        sn: index + 1,
      }));
  }, [designations]);

  const getRowProps = (_state: object, rowInfo?: TableRowInfo) => {
    return {
      className: 'align-items-center d-flex',
      onClick: (e: React.MouseEvent) => {
        if (!permissions[DESIGNATIONS.UPDATE]) {
          return;
        }

        const id = rowInfo?.original?.id;
        if (typeof id !== 'number') {
          return;
        }

        const url = interpolate(UPDATE_DESIGNATION, { id });

        if (e.ctrlKey || e.metaKey) {
          window.open(url);
        } else {
          history.push(url);
        }
      },
    };
  };

  const handleEdit = useCallback((row: ActionCellProps) => {
    history.push(interpolate(UPDATE_DESIGNATION, { id: row.original.id }));
  }, []);

  const handleDelete = useCallback(
    (row: ActionCellProps) => {
      deleteDesignation(row.original.id, {
        onSuccess: () => {
          toast.success({ title: 'Success', message: 'Designation deleted successfully.' });
        },
      });
    },
    [deleteDesignation]
  );

  const deleteColumn = useMemo<Column<DesignationRow>>(() => {
    const canEdit = Boolean(permissions[DESIGNATIONS.UPDATE]);
    const canDelete = Boolean(permissions[DESIGNATIONS.DELETE]);
    const canEditOrDelete = canEdit || canDelete;

    return {
      Header: '',
      width: 6,
      className: 'margin-left-auto cursor-default',
      Cell: (row: ActionCellProps) =>
        canEditOrDelete ? (
          <span
            className="verticle-ellipse cursor-pointer"
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
            }}
          >
            <PopOver
              interactive
              open={openPopoverId === row.original.id}
              onRequestClose={() => setOpenPopoverId(null)}
              theme="light"
              size="regular"
              customClass="overflow-menu__regular"
              position="left-start"
              disableOnScroll={true}
              popperOptions={{
                modifiers: {
                  addZIndex: {
                    enabled: true,
                    order: 810,
                    fn: (data: { styles: Record<string, string | number> }) => ({
                      ...data,
                      styles: {
                        ...data.styles,
                        zIndex: 10,
                      },
                    }),
                  },
                },
              }}
              html={
                <EditDelete
                  onClose={() => setOpenPopoverId(null)}
                  onDelete={() => handleDelete(row)}
                  onEdit={() => handleEdit(row)}
                  canEdit={canEdit}
                  canDelete={canDelete}
                  type="designation"
                  title={row.original.name}
                />
              }
            >
              <span
                onClick={(e) => {
                  e.stopPropagation();
                  setOpenPopoverId(openPopoverId === row.original.id ? null : row.original.id);
                }}
              >
                <BsThreeDotsVertical size={20} />
              </span>
            </PopOver>
          </span>
        ) : null,
    };
  }, [handleDelete, handleEdit, permissions, openPopoverId]);

  const columns = useMemo<Array<Column<DesignationRow>>>(() => {
    return [
      {
        Header: (
          <TableHeader
            label="SN."
            identifier="sn"
            columnToSortBy={columnToSortBy}
            isAscendingOrdered={isAscendingOrdered}
            onClick={() => {
              if (columnToSortBy !== 'sn') {
                setIsAscendingOrdered(true);
              } else {
                setIsAscendingOrdered(!isAscendingOrdered);
              }
              setColumnToSortBy('sn');
            }}
          />
        ),
        accessor: 'sn',
        maxWidth: 72,
        Cell: (props: CellProps) => {
          return (
            <span className="capital-text employees__table-text">
              {props.value < 10 ? `0${props.value}` : props.value}
            </span>
          );
        },
      },
      {
        Header: (
          <TableHeader
            label="Name"
            identifier="name"
            columnToSortBy={columnToSortBy}
            isAscendingOrdered={isAscendingOrdered}
            onClick={() => {
              if (columnToSortBy !== 'name') {
                setIsAscendingOrdered(true);
              } else {
                setIsAscendingOrdered(!isAscendingOrdered);
              }
              setColumnToSortBy('name');
            }}
          />
        ),
        accessor: 'name',
        maxWidth: 500,
        Cell: ({ original }: { original: DesignationRow }) => {
          return <span className="text-bold capital-text">{original.name}</span>;
        },
      },
      {
        Header: (
          <TableHeader
            label="Description"
            identifier="description"
            columnToSortBy={columnToSortBy}
            isAscendingOrdered={isAscendingOrdered}
            onClick={() => {
              if (columnToSortBy !== 'description') {
                setIsAscendingOrdered(true);
              } else {
                setIsAscendingOrdered(!isAscendingOrdered);
              }
              setColumnToSortBy('description');
            }}
          />
        ),
        accessor: 'description',
        maxWidth: 600,
      },
    ];
  }, [columnToSortBy, isAscendingOrdered]);

  const defaultSorted = useMemo(() => {
    return [
      {
        id: 'sn',
        desc: false,
      },
    ];
  }, []);

  return (
    <div className="full-scope-card">
      <div className="full-scope-card__header table-header name-wrap">
        <div>
          <div className="table-title">Designations</div>
          <div className="table-subtitle">
            Showing {tableData.length} {pluralize('Result', tableData.length)}
          </div>
        </div>
        {permissions[DESIGNATIONS.CREATE] && (
          <Link className="f-left add-employee-button" to={CREATE_DESIGNATION}>
            Add Designation
          </Link>
        )}
      </div>

      <div className="full-scope-card__content">
        <Table
          data={tableData}
          columns={[...columns, deleteColumn]}
          loading={isLoading}
          defaultSorted={defaultSorted}
          showPagination={false}
          noDataText={'Designation Not Found'}
          minRows={tableData.length}
          getTrProps={getRowProps}
        />
      </div>
    </div>
  );
}

export default Designation;
